import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, children, disabled, ...props }, ref) => {

    const baseStyles = 'inline-flex items-center justify-center rounded-lg font-medium transition-[transform,background-color,border-color,color,box-shadow,opacity] duration-150 ease-snap focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-domino-ivory disabled:cursor-not-allowed select-none active:scale-[0.98]';

    const variants = {
      primary: 'border border-transparent bg-domino-aubergine text-white shadow-[0_10px_22px_rgba(74,31,78,0.18)] hover:bg-domino-prune focus:ring-domino-prune disabled:bg-domino-aubergine disabled:text-white disabled:opacity-70 disabled:shadow-none',
      secondary: 'border border-domino-cardBorder bg-domino-white text-domino-ink shadow-sm hover:border-domino-aubergine hover:bg-domino-muted focus:ring-domino-prune disabled:border-domino-border disabled:bg-domino-muted disabled:text-domino-inkSoft disabled:opacity-100 disabled:shadow-none',
      outline: 'border border-domino-aubergine bg-transparent text-domino-aubergine hover:bg-domino-plumTint focus:ring-domino-prune disabled:border-domino-border disabled:bg-domino-muted disabled:text-domino-inkSoft disabled:opacity-100',
      ghost: 'bg-transparent text-domino-inkSoft hover:bg-domino-plumTint hover:text-domino-aubergine focus:ring-domino-prune disabled:text-domino-inkSoft disabled:opacity-70',
      danger: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500 shadow-sm disabled:opacity-70',
    };

    const sizes = {
      sm: 'h-8 px-3 text-xs',
      md: 'h-9 px-4 text-sm',
      lg: 'h-10 px-6 text-base',
      icon: 'h-9 w-9 p-0',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>{children}</span>
          </div>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
